import { ApiError, type AppPlatform, type AppReleaseRow, type AppVersionRow } from '@hq/api-sdk';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  QRCode,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { sumWidth, TipText } from '@hq/ui-kit';
import { buildAppsUrl, deleteAppFile, isLoopbackUrl, probeAppsUploadService, uploadAppFile } from '../appsStore';
import { hq } from '../session';

interface UploadValues {
  appKey: string;
  platform: AppPlatform;
  version: string;
  buildNumber?: number;
  description?: string;
  forceUpdate: boolean;
}

/** 字节数友好显示。 */
function formatSize(bytes: number): string {
  if (!bytes) return '-';
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

/** dataURL → Blob（导出 antd QRCode 画布后上传）。 */
function dataUrlToBlob(dataUrl: string): Blob {
  const commaIndex = dataUrl.indexOf(',');
  const head = commaIndex >= 0 ? dataUrl.slice(0, commaIndex) : '';
  const body = commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
  const mime = /:(.*?);/.exec(head)?.[1] ?? 'image/png';
  const bin = atob(body);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Blob([buf], { type: mime });
}

/**
 * App 上传管理（sys_app_release）：安装包→站点本地存 apps → 生成下载二维码 → 登记元数据到后端。
 * 文件不经过后端（由 web-system 站点本地通道存/取）；后端只存元数据。
 * 参考旧项目 hsmsx_webf #/setting/versionupload；二维码由前端 antd QRCode 依下载地址生成，
 * 再导出 PNG 存回本地 apps（无需后端二维码库）。当前发布是端侧检查更新的生效来源。
 */
export default function AppReleases() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [appKey, setAppKey] = useState('');
  const [platform, setPlatform] = useState<AppPlatform | ''>('');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [packageFile, setPackageFile] = useState<File | null>(null);
  const [form] = Form.useForm<UploadValues>();
  // 上传通道探活：通道不可用（生产未起 upload-server / 静态托管未接同源通道）时提前提示，而不是等上传报错
  const [channel, setChannel] = useState<'probing' | 'http' | 'unavailable'>('probing');

  useEffect(() => {
    let alive = true;
    void probeAppsUploadService().then((mode) => {
      if (alive) setChannel(mode);
    });
    return () => {
      alive = false;
    };
  }, []);

  // 二维码弹窗
  const [qrRow, setQrRow] = useState<AppReleaseRow | null>(null);
  const [qrSaving, setQrSaving] = useState(false);
  const qrWrapRef = useRef<HTMLDivElement>(null);

  const versions = useQuery({
    queryKey: ['app-versions', 'releases-picker'],
    queryFn: () => hq.appVersion.version.list('')
  });

  const releases = useQuery({
    queryKey: ['app-releases', appKey, platform],
    queryFn: () => hq.appVersion.release.list(appKey, platform)
  });
  const rows = releases.data ?? [];
  const loading = releases.isLoading || releases.isFetching;

  const load = () => queryClient.invalidateQueries({ queryKey: ['app-releases'] });

  const openUpload = () => {
    form.resetFields();
    form.setFieldsValue({ platform: 'android', buildNumber: 0, forceUpdate: false, appKey: appKey || undefined });
    setPackageFile(null);
    setUploadOpen(true);
  };

  const submitUpload = async () => {
    const values = await form.validateFields();
    const key = values.appKey.trim();
    if (values.platform === 'android' && !packageFile) {
      message.error('安卓发布必须选择安装包文件');
      return;
    }
    setSaving(true);
    const host = (versions.data ?? []).find((v: AppVersionRow) => v.appKey === key)?.downloadHost ?? undefined;
    // 已落盘的文件名：登记元数据失败时回滚，避免 apps 目录残留孤儿安装包
    let savedName: string | null = null;
    try {
      // 1) 安装包先存到站点本地 apps（不经过后端），拿到落地文件名/大小
      let fileName: string | null = null;
      let fileUrl: string | null = null;
      let fileSize = 0;
      if (packageFile) {
        const saved = await uploadAppFile(packageFile, packageFile.name);
        savedName = saved.fileName;
        fileName = saved.fileName;
        fileUrl = buildAppsUrl(saved.fileName, host);
        fileSize = saved.size;
        if (isLoopbackUrl(fileUrl)) {
          message.warning('未配置下载对外域名，地址按本机 localhost 落库，手机扫码/下载会失败；请先在「App 版本管理」填写下载对外域名');
        }
      }
      // 2) 上报元数据登记（后端只落库，不收发文件）
      const result = await hq.appVersion.release.record({
        appKey: key,
        platform: values.platform,
        version: values.version.trim(),
        buildNumber: values.buildNumber ?? 0,
        description: values.description?.trim() || null,
        forceUpdate: values.forceUpdate,
        fileName,
        fileUrl,
        fileSize,
        originalName: packageFile?.name ?? null
      });
      message.success('安装包已保存到本地 apps 并登记发布');
      setUploadOpen(false);
      await load();
      // 登记成功即引导生成二维码（下载地址此时已确定）
      if (result.fileUrl) {
        setQrRow({
          id: result.id,
          appKey: result.appKey,
          platform: result.platform,
          version: result.version,
          buildNumber: 0,
          fileUrl: result.fileUrl,
          isCurrent: 0,
          forceUpdate: 0,
          fileSize
        } as AppReleaseRow);
      }
    } catch (err) {
      if (savedName) await deleteAppFile(savedName);
      message.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const setCurrent = async (row: AppReleaseRow) => {
    try {
      await hq.appVersion.release.setCurrent(row.id);
      message.success(`已设为当前发布（${row.platform} ${row.version}）`);
      await load();
      void queryClient.invalidateQueries({ queryKey: ['app-versions'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    }
  };

  const remove = async (row: AppReleaseRow) => {
    try {
      await hq.appVersion.release.remove(row.id);
      // 后端软删记录；本地 apps 物理文件由站点同步清理（尽力而为）
      if (row.fileName) await deleteAppFile(row.fileName);
      if (row.qrFileName) await deleteAppFile(row.qrFileName);
      message.success('已删除发布（含本地 apps 文件）');
      await load();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    }
  };

  const saveQr = async () => {
    if (!qrRow?.fileUrl) return;
    const canvas = qrWrapRef.current?.querySelector('canvas');
    if (!canvas) {
      message.error('二维码尚未渲染完成，请稍候再试');
      return;
    }
    setQrSaving(true);
    try {
      const blob = dataUrlToBlob(canvas.toDataURL('image/png'));
      const host = (versions.data ?? []).find((v: AppVersionRow) => v.appKey === qrRow.appKey)?.downloadHost ?? undefined;
      // 二维码 PNG 先存本地 apps，再向后端上报文件名+地址
      const saved = await uploadAppFile(blob, `${qrRow.appKey}-${qrRow.version}-qr.png`);
      const qrUrl = buildAppsUrl(saved.fileName, host);
      await hq.appVersion.release.attachQr(qrRow.id, { qrFileName: saved.fileName, qrUrl });
      message.success('二维码已保存到本地 apps 并回写发布');
      setQrRow(null);
      await load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err));
    } finally {
      setQrSaving(false);
    }
  };

  const columns: ColumnsType<AppReleaseRow> = [
    { title: 'AppKey', dataIndex: 'appKey', width: 150, ellipsis: { showTitle: false }, render: (v: string) => <TipText text={v} /> },
    {
      title: '平台',
      dataIndex: 'platform',
      width: 90,
      render: (v: string) => <Tag color={v === 'android' ? 'green' : 'blue'}>{v}</Tag>
    },
    { title: '版本号', dataIndex: 'version', width: 110 },
    { title: 'Build', dataIndex: 'buildNumber', width: 80 },
    { title: '大小', dataIndex: 'fileSize', width: 100, render: (v: number) => formatSize(v) },
    { title: '更新说明', dataIndex: 'description', width: 180, ellipsis: { showTitle: false }, render: (v: string | null) => (v ? <TipText text={v} /> : '-') },
    {
      title: '状态',
      key: 'flags',
      width: 150,
      render: (_v, row) => (
        <Space size={4}>
          {row.isCurrent === 1 ? <Tag color="gold">当前</Tag> : null}
          {row.forceUpdate === 1 ? <Tag color="red">强制</Tag> : null}
          {row.fileUrl ? (
            <Typography.Link href={row.fileUrl} target="_blank" rel="noreferrer">
              下载
            </Typography.Link>
          ) : null}
        </Space>
      )
    },
    {
      title: '二维码',
      dataIndex: 'qrUrl',
      width: 80,
      render: (v: string | null) => (v ? <img src={v} alt="qr" style={{ width: 48, height: 48 }} /> : '-')
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_v, row) => (
        <Space size={4}>
          {row.isCurrent !== 1 ? (
            <Popconfirm title="设为当前发布将回写版本号与下载地址，确认？" onConfirm={() => void setCurrent(row)}>
              <Button size="small" type="primary">
                设为当前
              </Button>
            </Popconfirm>
          ) : (
            <Tag color="gold">生效中</Tag>
          )}
          {row.fileUrl ? (
            <Button size="small" onClick={() => setQrRow(row)}>
              {row.qrUrl ? '重生成二维码' : '生成二维码'}
            </Button>
          ) : null}
          <Popconfirm title="删除会同时移除 apps 目录中的文件，确认？" onConfirm={() => void remove(row)}>
            <Button size="small" danger type="link">
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card
      title="App 上传管理"
      extra={
        <Space size={8}>
          <Select
            style={{ width: 220 }}
            placeholder="全部 App"
            allowClear
            showSearch
            optionFilterProp="label"
            value={appKey || undefined}
            onChange={(v) => setAppKey(v ?? '')}
            options={(versions.data ?? []).map((item) => ({ value: item.appKey, label: `${item.appName}（${item.appKey}）` }))}
          />
          <Select
            style={{ width: 130 }}
            placeholder="全部平台"
            allowClear
            value={platform || undefined}
            onChange={(v) => setPlatform(v ?? '')}
            options={[
              { value: 'android', label: 'android' },
              { value: 'ios', label: 'ios' }
            ]}
          />
          <Button onClick={() => void load()} loading={loading}>
            刷新
          </Button>
          <Button type="primary" onClick={openUpload} disabled={channel === 'unavailable'}>
            上传安装包
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="安装包由本站点本地存到「apps 目录」（不经过后端）并得到下载地址；再据该地址生成下载二维码并存回本地。后端只登记元数据。设为当前发布后，端侧检查更新即生效。"
      />
      {channel === 'unavailable' ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="本地上传通道不可用（/local-upload/apps/healthz 无响应），已禁用上传"
          description="安装包/二维码/logo 由 web-system 站点自己存到 web-system/apps。dev 请用 start-system 启动；生产请在同一站点进程里带上通道：node upload-server.mjs --port 5010 --dist dist，并确保 /apps 与 /local-upload/apps 不被静态托管抢先接管。"
        />
      ) : null}
      <Table<AppReleaseRow>
        size="small"
        rowKey="id"
        loading={loading}
        pagination={false}
        dataSource={rows}
        scroll={{ x: sumWidth(columns) }}
        columns={columns}
      />
      {releases.error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message={releases.error instanceof ApiError ? releases.error.friendlyMessage : String(releases.error)}
        />
      ) : null}

      <Modal
        title="上传安装包"
        open={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        onOk={() => void submitUpload()}
        confirmLoading={saving}
        okText="上传"
        width={560}
        destroyOnHidden
      >
        <Form<UploadValues> form={form} layout="vertical">
          <Form.Item name="appKey" label="App" rules={[{ required: true, message: '请选择 App' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="选择 App"
              options={(versions.data ?? []).map((item) => ({ value: item.appKey, label: `${item.appName}（${item.appKey}）` }))}
            />
          </Form.Item>
          <Form.Item name="platform" label="平台" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'android', label: 'android（上传 apk）' },
                { value: 'ios', label: 'ios（可只走商店链接，不传包）' }
              ]}
            />
          </Form.Item>
          <Space style={{ width: '100%' }} size={12}>
            <Form.Item name="version" label="版本号" rules={[{ required: true, message: '如 1.2.3' }]} style={{ width: '55%' }}>
              <Input maxLength={32} placeholder="如 1.2.3" />
            </Form.Item>
            <Form.Item name="buildNumber" label="Build" style={{ width: '40%' }}>
              <Input type="number" placeholder="0" />
            </Form.Item>
          </Space>
          <Form.Item label="安装包（apk / ipa）" extra="安卓必填；文件直接存到本站点本地 apps 目录">
            <Upload
              accept=".apk,.ipa,application/vnd.android.package-archive"
              maxCount={1}
              showUploadList
              beforeUpload={(f) => {
                setPackageFile(f);
                return false; // 不自动上传，随表单一起提交
              }}
              onRemove={() => setPackageFile(null)}
            >
              <Button>选择文件</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="description" label="更新说明">
            <Input.TextArea rows={2} maxLength={500} placeholder="端侧更新弹窗展示" />
          </Form.Item>
          <Form.Item name="forceUpdate" label="强制更新" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`下载二维码 · ${qrRow?.appKey ?? ''} ${qrRow?.version ?? ''}`}
        open={Boolean(qrRow)}
        onCancel={() => setQrRow(null)}
        footer={[
          <Button key="cancel" onClick={() => setQrRow(null)}>
            取消
          </Button>,
          <Button key="save" type="primary" loading={qrSaving} onClick={() => void saveQr()}>
            保存二维码到 apps
          </Button>
        ]}
        width={360}
      >
        <div style={{ textAlign: 'center' }}>
          <Typography.Paragraph type="secondary" style={{ wordBreak: 'break-all' }}>
            {qrRow?.fileUrl}
          </Typography.Paragraph>
          <div ref={qrWrapRef} style={{ display: 'inline-block' }}>
            {qrRow?.fileUrl ? <QRCode value={qrRow.fileUrl} type="canvas" size={220} /> : null}
          </div>
          <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
            扫码即可在手机上打开下载链接；保存后二维码与安装包同存于 apps 目录。
          </Typography.Paragraph>
        </div>
      </Modal>
    </Card>
  );
}
